import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, RefreshCw, UserPlus, Trash2, Radar, X } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { ProvisionProgressModal } from '../components/ProvisionProgressModal';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

function timeAgo(ts: string) {
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export default function PnpPhones() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignTarget, setAssignTarget] = useState<any | null>(null);
  const [provisioning, setProvisioning] = useState<{ mac: string; label?: string } | null>(null);
  const [scan, setScan] = useState<any | null>(null);
  const scanPollRef = useRef<any>(null);
  const [phonesOnly, setPhonesOnly] = useState(true);
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'last_seen', dir: -1 });
  const [locFilter, setLocFilter] = useState('');

  const list = useQuery<any[]>({
    queryKey: ['discovered-phones'],
    queryFn: () => fJson('/api/phones/discovered'),
    refetchInterval: 5000,
  });
  const users = useQuery<any[]>({
    queryKey: ['users-list'],
    queryFn: () => fJson('/api/users'),
  });
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  // A phone = SIP evidence (model / OPTIONS User-Agent / SIP-PnP-active_scan hit), already assigned,
  // OR a device from a known phone vendor (Grandstream vendor_key, or a phone-vendor MAC OUI). The OUI
  // signal matters for onboarding: a just-plugged-in / factory-reset phone has no SIP account yet, so
  // it never answers OPTIONS and is only seen via ARP — excluding OUI-only hid exactly the phones you
  // came to add. The rare cost is a Grandstream GWN AP/ATA showing under "Phones only"; worth it.
  const PHONE_OUIS = ['ec74d7', 'c074ad', '000b82', '00260b', 'accf23', '2c54cf', 'acd9cc', '001565', '000fd5', '805ec0', '0004f2', '64167f', '00907a', 'e0d173'];
  const isPhone = (p: any) =>
    !!(p.model && p.model.toLowerCase() !== 'unknown')
    || !!(p.sip_user_agent && String(p.sip_user_agent).trim())
    || /sip|pnp|active_scan/i.test(p.discovered_via || '')
    || p.status === 'assigned'
    || (p.vendor_key && String(p.vendor_key).toLowerCase() === 'grandstream')
    || PHONE_OUIS.includes(String(p.mac_address || '').replace(/[^a-f0-9]/gi, '').slice(0, 6).toLowerCase());
  // Discovery noise: multicast / reserved 224-239.x are NOT real devices.
  const isMulticast = (ip: any) => { const o = parseInt(String(ip || '').split('.')[0], 10); return o >= 224 && o <= 239; };
  // Location dropdown options = real named locations (SBC / department), never raw subnets.
  const locations = Array.from(new Set((list.data || []).filter((p: any) => !isMulticast(p.ip_address)).map((p: any) => p.sbc_name).filter(Boolean))).sort();
  // Location name -> SBC node id (from any row that carries one) so Scan can target
  // that SBC's own LAN sweep instead of pointlessly sweeping the PBX's local subnets.
  const locNodeId: Record<string, string> = {};
  for (const p of (list.data || [])) {
    if (p.sbc_name && p.sbc_node_id && !locNodeId[p.sbc_name]) locNodeId[p.sbc_name] = p.sbc_node_id;
  }
  const filtered = (list.data || [])
    .filter(p => !isMulticast(p.ip_address))
    .filter(p => !phonesOnly || isPhone(p))
    .filter(p => !locFilter || p.sbc_name === locFilter)
    .filter(p =>
      !search || (p.mac_address || '').toLowerCase().includes(search.toLowerCase())
              || (p.model || '').toLowerCase().includes(search.toLowerCase())
              || (p.vendor_key || '').toLowerCase().includes(search.toLowerCase()));

  // Sortable columns — click a header. IP sorts numerically, When by timestamp, rest A-Z.
  function sortVal(p: any, key: string): number | string {
    if (key === 'last_seen') return p.last_seen ? new Date(p.last_seen).getTime() : 0;
    if (key === 'ip_address') {
      const o = String(p.ip_address || '').split('.').map(Number);
      return o.length === 4 && o.every(n => !isNaN(n)) ? (o[0] * 2 ** 24 + o[1] * 2 ** 16 + o[2] * 2 ** 8 + o[3]) : 0;
    }
    if (key === 'location') return (p.sbc_name || p.ip_address || '').toLowerCase();
    return String(p[key] || '').toLowerCase();
  }
  const sorted = [...filtered].sort((a, b) => {
    const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
    return va < vb ? -sort.dir : va > vb ? sort.dir : 0;
  });
  const toggleSort = (key: string) =>
    setSort(s => (s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { key, dir: 1 }));

  const del = useMutation({
    mutationFn: async () => {
      for (const id of selected) await fJson(`/api/phones/discovered/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['discovered-phones'] }); setSelected(new Set()); toast('success', 'Removed from discovery'); },
  });

  const assign = useMutation({
    mutationFn: ({ id, ext, dom, label, model }: any) => fJson('/api/phones/assign', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discovered_phone_id: id, extension_uuid: ext, domain_uuid: dom, label, model }),
    }),
    onSuccess: (data: any, vars: any) => {
      qc.invalidateQueries({ queryKey: ['discovered-phones'] });
      setAssignTarget(null);
      if (data?.mac_address) {
        // Open the live progress modal instead of a fire-and-forget toast — the phone still has to
        // receive its config, reboot, and register, which the modal tracks to completion.
        setProvisioning({ mac: data.mac_address, label: vars?.label || (data.extension ? `Extension ${data.extension}` : undefined) });
      } else {
        toast('success', 'Assigned');
      }
    },
    onError: (e: any) => toast('error', e.message),
  });

  // Active LAN scan: kick off, then poll progress every 1s until done.
  // With a Location selected, the scan targets THAT SBC (it sweeps its own remote
  // LAN via the heartbeat scan_now channel) instead of the PBX's local subnets —
  // the PBX can't see a branch office's network, so the old global sweep never
  // found remote phones and probed every other site for nothing.
  const startScan = useMutation({
    mutationFn: () => fJson('/api/phones/active-scan', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(locFilter && locNodeId[locFilter] ? { sbc_node_id: locNodeId[locFilter] } : {}),
    }),
    onSuccess: (r: any) => {
      setScan({ id: r.scan_id, mode: r.mode, sbc_name: r.sbc_name, scanned: 0, found: 0, candidates: 0, done: false });
      scanPollRef.current = setInterval(async () => {
        try {
          const s = await fJson(`/api/phones/active-scan/${r.scan_id}`);
          setScan(s);
          qc.invalidateQueries({ queryKey: ['discovered-phones'] });
          if (s.done) {
            clearInterval(scanPollRef.current);
            scanPollRef.current = null;
            toast('success', s.mode === 'sbc'
              ? `${s.sbc_name} sweep complete — ${s.found} phone${s.found === 1 ? '' : 's'} reported`
              : `Scan complete — ${s.found} phone${s.found === 1 ? '' : 's'} found from ${s.scanned} hosts probed`);
          }
        } catch (e: any) {
          clearInterval(scanPollRef.current);
          scanPollRef.current = null;
          setScan((cur: any) => cur ? { ...cur, done: true, error: e.message } : null);
        }
      }, 1000);
    },
    onError: (e: any) => toast('error', `Scan failed: ${e.message}`),
  });

  // Cleanup on unmount
  useEffect(() => () => { if (scanPollRef.current) clearInterval(scanPollRef.current); }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/phones')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Phones</button>
        <h1 className="text-xl font-semibold">PNP Phones</h1>
        <a href="#" className="ml-auto text-primary text-sm hover:underline">Help</a>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <button
          onClick={() => startScan.mutate()}
          disabled={startScan.isPending || (scan && !scan.done)}
          className="btn-primary inline-flex items-center gap-1"
          title={locFilter && locNodeId[locFilter]
            ? `Ask the ${locFilter} SBC to sweep its own LAN for phones`
            : 'Probe the local network for phones (ARP table + SIP OPTIONS sweep)'}
        >
          <Radar size={14} className={scan && !scan.done ? 'animate-pulse' : ''} />
          {scan && !scan.done
            ? (scan.mode === 'sbc' ? `Sweeping ${scan.sbc_name}… ${scan.found} found` : `Scanning… ${scan.scanned} probed, ${scan.found} found`)
            : (locFilter && locNodeId[locFilter] ? `Scan ${locFilter}` : 'Scan')}
        </button>
        <button
          disabled={selected.size !== 1}
          onClick={() => {
            const id = [...selected][0];
            setAssignTarget(filtered.find(p => p.id === id));
          }}
          className="btn-primary inline-flex items-center gap-1"><UserPlus size={14} /> Assign</button>
        <button disabled={!selected.size} onClick={() => del.mutate()} className="btn-primary inline-flex items-center gap-1 bg-danger"><Trash2 size={14} /> Delete</button>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['discovered-phones'] })} className="btn-ghost inline-flex items-center gap-1"><RefreshCw size={14} /> Refresh</button>
        <label className="inline-flex items-center gap-1.5 text-sm text-textSecondary ml-1 cursor-pointer select-none" title="Hide bare network devices that didn't identify as phones">
          <input type="checkbox" checked={phonesOnly} onChange={e => setPhonesOnly(e.target.checked)} /> Phones only
        </label>

        {scan && (
          <div className={`text-xs ml-2 px-2 py-1 rounded-md font-mono ${scan.done ? (scan.error ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success') : 'bg-primary/10 text-primary'}`}>
            {scan.error
              ? `error: ${scan.error}`
              : scan.mode === 'sbc'
                ? (scan.done
                    ? `${scan.sbc_name} sweep — ${scan.found} phones reported`
                    : `sweeping ${scan.sbc_name} — ${scan.found} found (results appear live)`)
                : scan.done
                  ? `complete — ${scan.found} phones found, ${scan.scanned} hosts probed`
                  : `scan ${scan.id?.slice(0,6) || ''} — ${scan.scanned} probed, ${scan.found} found`}
            {scan.done && (
              <button className="ml-2 hover:opacity-70" onClick={() => setScan(null)}><X size={10} className="inline" /></button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Search size={16} className="text-textSecondary" />
        <input className="input flex-1" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-textSecondary">Location:</span>
        <select className="input w-auto min-w-[200px]" value={locFilter} onChange={e => setLocFilter(e.target.value)}>
          <option value="">All locations</option>
          {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
        </select>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr>
            <th></th>
            {([['Vendor','vendor_key'],['Model','model'],['Fw Version','firmware'],['MAC','mac_address'],['When','last_seen'],['IP','ip_address'],['Location','location']] as [string,string][]).map(([label, k]) => (
              <th key={k} className="cursor-pointer select-none hover:text-text" onClick={() => toggleSort(k)}>
                {label}{sort.key === k ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {sorted.map((p: any) => (
              <tr key={p.id} className="hover:bg-elevated">
                <td>
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => {
                    const next = new Set(selected);
                    if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                    setSelected(next);
                  }} />
                </td>
                <td>{p.vendor_key ? p.vendor_key.charAt(0).toUpperCase() + p.vendor_key.slice(1) : '—'}</td>
                <td>{p.model || '—'}</td>
                <td className="font-mono text-xs">{p.firmware || '1.0.x'}</td>
                <td className="font-mono text-xs">{(p.mac_address || '').replace(/:/g, '')}</td>
                <td className="text-xs">{p.last_seen ? timeAgo(p.last_seen) : '—'}</td>
                <td className="font-mono text-xs">{p.ip_address}</td>
                <td className="text-xs">{p.sbc_name
                  ? <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">{p.sbc_name}</span>
                  : <span className="text-textSecondary">{p.ip_address ? p.ip_address.split('.').slice(0, 3).join('.') + '.x' : '—'}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!sorted.length && (
          <div className="p-6 text-center text-textSecondary">
            {phonesOnly && (list.data || []).some((p: any) => !isPhone(p))
              ? <>No identified phones — {(list.data || []).filter((p: any) => !isPhone(p)).length} unidentified device(s) hidden. Uncheck <b>Phones only</b> to see everything the scan found.</>
              : 'No discovered phones. The SBC populates this list as phones register or appear on the LAN.'}
          </div>
        )}
      </div>

      <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} title="Assign Phone" size="md"
        footer={<><button onClick={() => setAssignTarget(null)} className="btn-ghost">Cancel</button><button onClick={() => {
          const t = assignTarget;
          assign.mutate({ id: t._id, ext: t._ext, dom: t._dom, label: t._label, model: t._model });
        }} disabled={!assignTarget?._ext || !assignTarget?._model || assign.isPending} className="btn-primary disabled:opacity-50">{assign.isPending ? 'Assigning…' : 'Assign'}</button></>}>
        {assignTarget && (
          <AssignForm target={assignTarget} setTarget={setAssignTarget} users={users.data || []} tenants={tenants.data || []} />
        )}
      </Dialog>

      {provisioning && (
        <ProvisionProgressModal
          mac={provisioning.mac}
          label={provisioning.label}
          onClose={() => { setProvisioning(null); qc.invalidateQueries({ queryKey: ['discovered-phones'] }); }}
        />
      )}
    </div>
  );
}

function AssignForm({ target, setTarget, users, tenants }: any) {
  const [dom, setDom] = useState(target.bound_domain_uuid || '');
  const [ext, setExt] = useState('');
  const [label, setLabel] = useState('');
  const [model, setModel] = useState('');
  const inDept = users.filter((u: any) => u.domain_uuid === dom);
  return (
    <div className="space-y-3">
      <div className="text-xs text-textSecondary">
        Phone: <span className="font-mono">{target.mac_address}</span> ({target.vendor_key} {target.model})
      </div>
      <div>
        {/* Auto-detect is unreliable (often 'unknown'), so the admin picks the model — this selects
            the provisioning template. Without it the phone reboots but never gets a config. */}
        <label className="block text-sm text-textSecondary mb-1">Phone model</label>
        <select className="input" value={model} onChange={e => { setModel(e.target.value); setTarget({ ...target, _id: target.id, _model: e.target.value }); }}>
          <option value="">— Choose model —</option>
          <option value="gs-1630">Grandstream GXP1630</option>
          <option value="gs-1628">Grandstream GXP1628</option>
          <option value="gs-2615">Grandstream GRP2615</option>
        </select>
      </div>
      <div>
        <label className="block text-sm text-textSecondary mb-1">Department</label>
        <select className="input" value={dom} onChange={e => { setDom(e.target.value); setExt(''); setTarget({ ...target, _id: target.id, _dom: e.target.value, _ext: '', _label: label }); }}>
          <option value="">— Choose —</option>
          {tenants.map((t: any) => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm text-textSecondary mb-1">Extension</label>
        <select className="input" value={ext} onChange={e => { setExt(e.target.value); setTarget({ ...target, _id: target.id, _dom: dom, _ext: e.target.value, _label: label }); }} disabled={!dom}>
          <option value="">— Choose —</option>
          {inDept.map((u: any) => <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm text-textSecondary mb-1">Label (optional)</label>
        <input className="input" value={label} onChange={e => { setLabel(e.target.value); setTarget({ ...target, _label: e.target.value }); }} placeholder="Front desk Yealink" />
      </div>
    </div>
  );
}
